import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ShieldCheck as ShieldCheckIcon, Info as InfoIcon, AlertTriangle as AlertTriangleIcon } from 'lucide-react';
import { isValidationRecent, formatValidationDate } from './confidenceUtils';

/**
 * ExpertValidationBadge - Shows expert validation status with coach info
 * Pixel-perfect port from Polymet expert-validation-badge.tsx
 * 
 * @param {Object} props
 * @param {Object} props.validation - Validation data
 * @param {boolean} props.validation.isValidated - Whether the valuation is validated
 * @param {boolean} [props.validation.isCurrentVersion] - Whether current valuation matches validated version
 * @param {Object} [props.validation.validatedBy] - Coach information
 * @param {string} [props.validation.validatedBy.coachName] - Coach name
 * @param {string} [props.validation.validatedBy.credentials] - Coach credentials
 * @param {string} [props.validation.validatedBy.coachAvatar] - Coach avatar URL
 * @param {string} [props.validation.validatedAt] - Validation timestamp
 * @param {string} [props.validation.programName] - Program name
 * @param {string} [props.validation.validationNotes] - Coach's notes
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Badge size
 * @param {boolean} [props.showDetails=true] - Whether to show hover details
 * @param {string} [props.className] - Additional CSS classes
 */
const ExpertValidationBadge = ({
  validation,
  size = 'md',
  showDetails = true,
  className = '',
}) => {
  const intl = useIntl();
  const [showPopover, setShowPopover] = useState(false);

  if (!validation?.isValidated) {
    return null;
  }

  const isRecent = isValidationRecent(validation.validatedAt);
  const isCurrentVersion = validation.isCurrentVersion !== false; // Default to true if not specified
  const isModified = !isCurrentVersion;

  // Color constants
  const colors = {
    emerald400: '#34D399',
    emerald500: '#10B981',
    emerald600: '#059669',
    teal500: '#14B8A6',
    amber500: '#F59E0B',
    amber600: '#D97706',
  };

  const sizeConfig = {
    sm: {
      container: { padding: '4px 8px', fontSize: '12px', gap: '4px' },
      icon: 14,
    },
    md: {
      container: { padding: '6px 12px', fontSize: '14px', gap: '6px' },
      icon: 16,
    },
    lg: {
      container: { padding: '8px 16px', fontSize: '16px', gap: '8px' },
      icon: 20,
    },
  };

  const sizeStyles = sizeConfig[size] || sizeConfig.md;

  // Different styling for modified vs current version
  const badgeStyle = isModified
    ? {
        background: `linear-gradient(to right, ${colors.amber500}, ${colors.amber600})`,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        border: `2px solid ${colors.amber500}80`,
        opacity: isRecent ? 1 : 0.75,
        padding: sizeStyles.container.padding,
        fontSize: sizeStyles.container.fontSize,
        gap: sizeStyles.container.gap,
      }
    : {
        background: `linear-gradient(to right, ${colors.emerald500}, ${colors.teal500})`,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        border: `2px solid ${colors.emerald400}80`,
        opacity: isRecent ? 1 : 0.75,
        padding: sizeStyles.container.padding,
        fontSize: sizeStyles.container.fontSize,
        gap: sizeStyles.container.gap,
      };

  const badge = (
    <div
      className={`inline-flex items-center rounded-full font-medium text-white ${className}`}
      style={badgeStyle}
    >
      {isModified ? (
        <AlertTriangleIcon size={sizeStyles.icon} className="flex-shrink-0" />
      ) : (
        <ShieldCheckIcon size={sizeStyles.icon} className="flex-shrink-0" />
      )}
      <span>
        {isModified
          ? intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.validated_modified' })
          : intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.expert_validated' })}
      </span>
      {showDetails && (
        <InfoIcon size={sizeStyles.icon - 2} className="flex-shrink-0 opacity-80" />
      )}
    </div>
  );

  if (!showDetails || !validation.validatedBy) {
    return badge;
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="rounded-full"
        style={{
          outline: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 2px ${isModified ? colors.amber500 : colors.emerald500}, 0 0 0 4px white`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'none';
          setTimeout(() => setShowPopover(false), 200);
        }}
        onClick={() => setShowPopover(!showPopover)}
      >
        {badge}
      </button>
      
      {/* Popover - positioned to the left to avoid viewport overflow */}
      {showPopover && (
        <div className="absolute z-50 top-full right-0 mt-2 w-80 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 space-y-4">
            {/* Modified Warning */}
            {isModified && (
              <div
                className="flex items-start gap-2 p-3 rounded-lg"
                style={{
                  backgroundColor: '#FFFBEB',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#FDE68A',
                }}
              >
                <AlertTriangleIcon
                  size={16}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: colors.amber600 }}
                />
                <div>
                  <div className="text-sm font-medium" style={{ color: '#92400E' }}>
                    {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.valuation_modified_title' })}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#B45309' }}>
                    {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.valuation_modified_description' })}
                  </div>
                </div>
              </div>
            )}

            {/* Coach Info */}
            <div className="flex items-start gap-3">
              {validation.validatedBy.coachAvatar ? (
                <img
                  src={validation.validatedBy.coachAvatar}
                  alt={validation.validatedBy.coachName}
                  className="w-12 h-12 rounded-full"
                  style={{ border: `2px solid ${colors.emerald500}` }}
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                  style={{ backgroundColor: colors.emerald500 }}
                >
                  {(validation.validatedBy.coachName || 'E')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-succession-text-primary">
                  {validation.validatedBy.coachName}
                </div>
                {validation.validatedBy.credentials && (
                  <div className="text-sm text-succession-text-secondary">
                    {validation.validatedBy.credentials}
                  </div>
                )}
              </div>
            </div>

            {/* Program Name */}
            {validation.programName && (
              <div>
                <div className="text-xs font-medium text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.program_completed_label' })}
                </div>
                <div className="text-sm text-succession-text-primary">
                  {validation.programName}
                </div>
              </div>
            )}

            {/* Validated Date */}
            {validation.validatedAt && (
              <div>
                <div className="text-xs font-medium text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.validated_on_label' })}
                </div>
                <div className="text-sm text-succession-text-primary">
                  {formatValidationDate(validation.validatedAt)}
                  {!isRecent && (
                    <span className="ml-2 text-xs" style={{ color: colors.amber600 }}>
                      {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.over_90_days_ago' })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Validation Notes */}
            {validation.validationNotes && (
              <div>
                <div className="text-xs font-medium text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.validation_notes_label' })}
                </div>
                <div className="text-sm text-succession-text-secondary">
                  {validation.validationNotes}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-3 border-t border-succession-border">
              <div className="flex items-start gap-2 text-xs text-succession-text-secondary">
                <ShieldCheckIcon
                  size={14}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: colors.emerald600 }}
                />
                <span>
                  {isModified
                    ? intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.footer_modified' })
                    : intl.formatMessage({ id: 'succession.confidence_rating.expert_validation.footer_validated' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertValidationBadge;
